import React from 'react';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/ru';
import {useParams, useNavigate} from "react-router-dom";
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import Link from '@mui/joy/Link';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import Button from '@mui/joy/Button';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Table from '@mui/joy/Table';
import Sheet from '@mui/joy/Sheet';
import Stack from "@mui/joy/Stack";
import ContentCopy from '@mui/icons-material/ContentCopy';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import Checkbox from '@mui/joy/Checkbox';
import Input from '@mui/joy/Input';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import VideogameAssetIcon from '@mui/icons-material/VideogameAsset';
import {NotificationService} from "../../../common/notification";
import {Game} from "../../../models/games";
import CircularProgress from "@mui/joy/CircularProgress";
import {botsListSkinsApi, botsSendSkinsApi} from "../botsApi";
import {gamesListApi} from "../../games/gamesApi";
import {getBotApi} from "../botsApi";
import { BotSkinInfo, BotTransferRequest, BotTransferResult } from '../../../models/bot';
import { OrderStatusDescription } from '../../../models/order';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import ListItemContent from "@mui/joy/ListItemContent";
import ListDivider from "@mui/joy/ListDivider";
import {formatDate} from "../../../common/dateTime";
import Chip from '@mui/material/Chip';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import RemoveDoneIcon from '@mui/icons-material/RemoveDone';

export function BotInventory() {
    const {id} = useParams();
    const botId = Number(id);
    const navigate = useNavigate();

    const [allGames, setAllGames] = React.useState<Game[]>([]);
    const [botGames, setBotGames] = React.useState<number[]>([]);
    const [isSending, setIsSending] = React.useState(false);
    const [selectedGame, setSelectedGame] = React.useState<number>(440);
    const [skins, setSkins] = React.useState<BotSkinInfo[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [baseDataLoading, setBaseDataLoading] = React.useState(false);
    const [open, setOpen] = React.useState(false);
    const [resultOpen, setResultOpen] = React.useState(false);
    const [selectedDate, setDate] = React.useState<Dayjs | null>(dayjs());
    const [tradeLink, setTradeLink] = React.useState<string>('');
    const [selectedSkins, setSelectedSkins] = React.useState<number[]>([]);
    const [transferResults, setTransferResults] = React.useState<BotTransferResult[]>([]);
    const [offerLink, setOfferLink] = React.useState<string>('');
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

    React.useEffect(() => {
        if (botId <= 0) {
            return;
        }

        setBaseDataLoading(true);
        try {
            loadAllGames();
            loadBotGames(botId);
        } finally {
            setBaseDataLoading(false);
        }
    }, [botId])

    const loadAllGames = async () => {
        let result = await gamesListApi();
        if (result.result) {
            setAllGames(result.value!)
        } else {
            NotificationService.alertError("Не удалось загрузить список игра. Попробуйте позже или обратитесь в службу поддержки")
        }
    }

    const loadBotGames = async (id: number) => {
        let result = await getBotApi(id);
        if (result.result) {
            setBotGames(result.value?.gamesInventoriesSize?.map(g => g.gameId) ?? []);
        } else {
            NotificationService.alertError("Не удалось получить игры бота. Попробуйте позже или обратитесь в службу поддержки")
        }
    }

    const onLoadSkins = async () => {
        setLoading(true);
        try {
            setSelectedSkins([]);

            let result = await botsListSkinsApi(botId, selectedGame);
            if (result.result) {
                const res = result.value!;
                setSkins(res);
            } else {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки")
            }
        } finally {
            setLoading(false);
        }
    }

    const getGameName = (id?: number): string => {
        if (!id)
            return '-';
        const gms = allGames.filter(p => p.id === id);
        if (gms.length !== 1)
            return '-';
        const game = gms[0];
        return game.name
    }

    const openOrder = (id: number) => {
        navigate(`/app/orders/${id}`);
    }

    const sendSkinsByDate = async () => {
        setIsSending(true);
        setTransferResults([]);
        try {
            const request = new BotTransferRequest(botId, selectedGame, tradeLink, selectedDate?.toDate(), []);
            let result = await botsSendSkinsApi([request]);
            if (result.result) {
                setTransferResults(result.value ?? []);
                setResultOpen(true);
            } else {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки")
            }
        } finally {
            setIsSending(false);
        }
    }

    const sendSkinsByСheck = async () => {
        setIsSending(true);
        setTransferResults([]);
        try {
            const request = new BotTransferRequest(botId, selectedGame, tradeLink, selectedDate?.toDate(), selectedSkins);
            let result = await botsSendSkinsApi([request]);
            if (result.result) {
                setTransferResults(result.value ?? []);
                setResultOpen(true);
            } else {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки")
            }
        } finally {
            setIsSending(false);
        }
    }

    const copyOfferLink = () => {
        navigator.clipboard.writeText(offerLink);
        NotificationService.alertSuccess("Скопировано!");
    }

    return (
        <Box
            component="main"
            className="MainContent"
            sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minWidth: 0,
                gap: 1,
            }}
        >
            {baseDataLoading ?
                <CircularProgress/> :
                <Box
                    className="SearchAndFilters-tabletUp"
                    sx={{
                        borderRadius: 'sm',
                        py: 2,
                        display: {xs: 'flex', sm: 'flex'},
                        flexWrap: 'wrap',
                        alignItems: 'end',
                        gap: 1.5,
                        '& > *': {
                            minWidth: {xs: '120px', md: '160px'},
                        },
                    }}
                >
                    <FormControl size="sm" sx={{
                        justifyContent: 'start',
                        minWidth: {xs: '100%', md: 'auto'},
                    }}>
                        <FormLabel>Игра</FormLabel>
                        <Select size="sm"
                                startDecorator={<VideogameAssetIcon/>}
                                placeholder="Игра"
                                value={selectedGame}
                                onChange={(_, value) => setSelectedGame(value ?? -1)}>
                            {allGames
                                .filter(function(obj) { return botGames.some(e => e === obj.id); })
                                .map((p) => <Option key={p.id} value={p.id}>{p.name}</Option>
                            )}
                        </Select>
                    </FormControl>
                    <FormControl size="sm" sx={{
                        justifyContent: 'end'
                    }}>
                        <Button size="sm" variant="outlined" color="neutral" onClick={onLoadSkins}
                                disabled={loading}>
                            Загрузить
                        </Button>
                    </FormControl>
                    <FormControl size="sm" sx={{
                        justifyContent: 'end'
                    }}>
                        <Button size="sm" variant="outlined" color="neutral" onClick={() => setOpen(true)}
                                disabled={isSending || loading}>
                            Передать скины
                        </Button>
                    </FormControl>
                    <Chip label={skins.length === selectedSkins.length ? "Снять выбор" : "Выбрать все"}
                        variant="outlined"
                        sx={{marginLeft: 'auto'}}
                        color={skins.length > 0 ? "success" : "default"}
                        disabled={skins.length <= 0}
                        icon={skins.length === selectedSkins.length ? <RemoveDoneIcon fontSize='medium' /> : <DoneAllIcon fontSize='medium' />}
                        onClick={skins.length === selectedSkins.length
                        ? async () => setSelectedSkins([])
                        : async () => setSelectedSkins(skins.map(e => e.assetId))}/>
                </Box>
            }
            {loading ?
                <CircularProgress/> :
                <>
                    <Sheet
                        className="OrderTableContainer"
                        variant="outlined"
                        sx={{
                            display: {xs: 'none', sm: 'initial'},
                            width: '100%',
                            borderRadius: 'sm',
                            flexShrink: 1,
                            overflow: 'auto',
                            height: 'auto',
                            maxHeight: '50dvh'
                        }}
                    >
                        <Table
                            aria-labelledby="tableTitle"
                            stickyHeader
                            hoverRow
                            sx={{
                                '--TableCell-headBackground': 'var(--joy-palette-background-level1)',
                                '--Table-headerUnderlineThickness': '1px',
                                '--TableRow-hoverBackground': 'var(--joy-palette-background-level1)',
                                '--TableCell-paddingY': '4px',
                                '--TableCell-paddingX': '8px',
                            }}
                        >
                            <thead>
                                <tr>
                                    <th style={{width: 100, padding: '12px 6px'}}></th>
                                    <th style={{width: 140, padding: '12px 6px'}}>Steam Id</th>
                                    <th style={{width: 240, padding: '12px 6px'}}>Имя</th>
                                    <th style={{width: 140, padding: '12px 6px'}}>Игра</th>
                                    <th style={{width: 140, padding: '12px 6px'}}>Дата получения</th>
                                    <th style={{width: 140, padding: '12px 6px'}}>Заказ#</th>
                                    <th style={{width: 140, padding: '12px 6px'}}>Выбрать для передачи</th>
                                </tr>
                            </thead>
                            <tbody>
                                {skins.map((row) => (
                                    <tr key={row.assetId}>
                                        <td>
                                            {row.image
                                                ? <Box component="img" src={row.image} sx={{ height: "30px", width: "auto" }} />
                                                : <></>}
                                        </td>
                                        <td>
                                            <Typography level="body-xs">{row.assetId}</Typography>
                                        </td>
                                        <td>
                                            <Typography level="body-xs">{row.hashName}</Typography>
                                        </td>
                                        <td>
                                            <Typography level="body-xs">{getGameName(row.gameId)}</Typography>
                                        </td>
                                        <td>
                                            <Typography level="body-xs">{formatDate(row.created)}</Typography>
                                        </td>
                                        <td>
                                            {row.sourceOrderId ?
                                                <Stack spacing={.5} direction={'column'}>
                                                    <Link level="body-sm" component="button" onClick={() => openOrder(row.sourceOrderId!)}>
                                                        #{row.sourceOrderId}
                                                    </Link>
                                                    <Typography level="body-xs" textColor={'background.backdrop'}>{OrderStatusDescription[row.sourceOrderStatus!]}</Typography>
                                                </Stack>
                                                : <>-</>
                                            }
                                        </td>
                                        <td>
                                            <Checkbox checked={selectedSkins.some(e => e === row.assetId)}
                                                        disabled={!row.canTrade}
                                                        onChange={(event) => setSelectedSkins(event.target.checked ?
                                                        [ ...selectedSkins, row.assetId] : selectedSkins.filter(e => e !== row.assetId)
                                            )}/>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Sheet>

                    <Box sx={{display: {xs: 'block', sm: 'none'}}}>
                        {skins.map((listItem) => (
                            <List key={listItem.assetId} size="sm" sx={{'--ListItem-paddingX': 0}}>
                                <ListItem
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'start',
                                    }}
                                >
                                    <ListItemContent sx={{display: 'flex', gap: 2, alignItems: 'start'}}>
                                        <div>
                                            <Typography gutterBottom sx={{fontWeight: 600}}>
                                                #{listItem.assetId}
                                            </Typography>
                                            <Typography level="body-xs">{listItem.hashName}</Typography>
                                            <Typography level="body-xs">Игра: {getGameName(listItem.gameId)}</Typography>
                                            {listItem.sourceOrderId
                                                ? <>
                                                    <Typography level="body-xs">Получено: {formatDate(listItem.created)}</Typography>
                                                    <Link level="body-sm" component="button" onClick={() => openOrder(listItem.sourceOrderId!)}>
                                                        Заказ #{listItem.sourceOrderId}
                                                    </Link>
                                                    <Typography level="body-xs">
                                                        Статус: {OrderStatusDescription[listItem.sourceOrderStatus!]}
                                                    </Typography>
                                                </>
                                                :<></>
                                            }
                                        </div>
                                    </ListItemContent>
                                    {listItem.image
                                        ? <Box component="img" src={listItem.image} sx={{ height: "30px", width: "auto" }} />
                                        : <></>}
                                    <Checkbox checked={selectedSkins.some(e => e === listItem.assetId)}
                                              disabled={!listItem.canTrade}
                                              onChange={(event) => setSelectedSkins(event.target.checked ?
                                              [ ...selectedSkins, listItem.assetId] : selectedSkins.filter(e => e !== listItem.assetId))}
                                    />
                                </ListItem>
                                <ListDivider/>
                            </List>
                        ))}
                    </Box>
                </>
            }

            <Dialog
                open={open}
                fullScreen={fullScreen}
                onClose={() => setOpen(false)}
            >
                <DialogTitle>Передача скинов</DialogTitle>
                <DialogContent>
                    <Alert variant="outlined" severity="info">Заказы по переданным скинам будут завершены со статусом 'Успех'</Alert>
                    <DialogContentText>
                        Выберите дату, скины полученные по которую (включительно), должны быть переданы, и укажите торговую ссылку получателя.
                    </DialogContentText>
                    {isSending
                        ? <CircularProgress/>
                        : <Box
                            noValidate
                            component="form"
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                width: '100%',
                            }}
                        >
                            <FormControl sx={{ mt: 2, minWidth: 120 }}>
                                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ru">
                                    <DatePicker
                                        sx={{mt: 2, maxWidth: 200, flex: 1}}
                                        label="Дата до"
                                        value={selectedDate}
                                        onChange={(newValue) => setDate(newValue)}
                                    />
                                </LocalizationProvider>
                            </FormControl>
                            <FormControl sx={{mt: 2, minWidth: '100%', flex: 1}} size="sm">
                                <FormLabel>Торговая ссылка получателя</FormLabel>
                                <Input size="sm" placeholder="Tradelink"
                                    value={tradeLink} onChange={(event) => setTradeLink(event.target.value)}/>
                            </FormControl>
                        </Box>}
                </DialogContent>
                <DialogActions>
                    <Button color="neutral" onClick={() => setOpen(false)}>
                        Закрыть
                    </Button>
                    <Button color="primary" onClick={sendSkinsByDate}
                            disabled={isSending || selectedDate === undefined || tradeLink.length === 0}>
                        Передать по дате
                    </Button>
                    <Button color="primary" onClick={sendSkinsByСheck} disabled={isSending || selectedSkins.length === 0 || tradeLink.length === 0}>
                        Передать отмеченные
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={resultOpen}
                fullScreen={fullScreen}
                onClose={() => setResultOpen(false)}
            >
                <DialogTitle>Результаты передачи</DialogTitle>
                <DialogContent>
                    <Box
                        noValidate
                        component="form"
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                            overflowY: 'scroll',
                            overflowX: 'hidden'
                        }}
                    >
                        {transferResults.map(row => (
                        <Stack spacing={.5} direction={'row'} alignItems={'center'} sx= {{ mt:1 }}>
                            <Typography level="body-xs" >Бот #{row.botId}</Typography>
                            {row.success
                                ? (row.steamOfferId ?? 0) > 0
                                    ? (<FormControl sx={{mt: 0, minWidth: '80%', maxWidth: '80%', flex: 1}} size="sm">
                                        <OutlinedInput
                                            type='text'
                                            sx={{boxSizing: 'border-box', overflow: 'hidden'}}
                                            size='small'
                                            readOnly
                                            value={`https://steamcommunity.com/tradeoffer/${row.steamOfferId}`}
                                            endAdornment={
                                            <InputAdornment position="end">
                                                <IconButton aria-label='Копировать'
                                                            onClick={() => {
                                                                setOfferLink(`https://steamcommunity.com/tradeoffer/${row.steamOfferId}`);
                                                                copyOfferLink();
                                                            }}
                                                            edge="end">
                                                    <ContentCopy />
                                                </IconButton>
                                            </InputAdornment>
                                            }
                                        />
                                    </FormControl>)
                                    : (<Typography level="body-xs" >Обмен был создан, но ссылку на обмен получить не удалось.</Typography>)
                                : <Typography level="body-xs">Ошибка создания обмена: {row.errorMessage ?? 'Unhandled error'}</Typography>}
                        </Stack>))}                        
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button color="neutral" onClick={() => setResultOpen(false)}>Закрыть</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
